import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { MdTypography, MdButton, MdIconPieSuccess, MdIconFailed } from '@antiqua-dls/vue-components';
import { Toaster } from 'src/utils/helper/toaster';
import SmartStars from 'src/components/icons/smart-stars';
import ToggleSwitch from 'src/components/form/toggle-switch/index.vue';
import {
  MIN_LENGTH_AI_ENABLED,
  MIN_LENGTH_FOR_SHORTEN_TEXT,
  AI_ACTION_MICRO_FEEDBACK_TIME,
  AI_TOGGLE_COLORS
} from 'src/utils/constants/common';

import AiConsentModal from '../../InfoModal/index.vue';

export default {
  name: 'EditorFooter',
  props: {
    config: {
      type: Object,
      required: true
    },
    isAiEnabled: {
      type: Boolean,
      default: false
    },
    isDiffView: {
      type: Boolean,
      default: false
    },
    editorValue: {
      type: String,
      default: ''
    },
    characterCount: {
      type: Object,
      default: () => ({
        limit: 600,
        editor: 0,
        aiSuggestion: 0
      })
    },
    aiResponseError: {
      type: Object,
      default: () => ({
        disableActions: false,
        errorMessage: null
      })
    },
    isAiResponseLoading: {
      type: Boolean,
      default: false
    },
    lastReplacedAction: {
      type: String,
      default: null
    },
    errorStatus: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-ai', 'discard', 'replace', 'ai-action'],
  components: {
    MdTypography,
    MdButton,
    SmartStars,
    MdIconPieSuccess,
    ToggleSwitch,
    MdIconFailed,
    AiConsentModal
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const hasBeenToggledOn = ref(props.isAiEnabled);
    const showInfoModal = ref(false);

    // Success states for each AI action
    const actionSuccessStates = ref({
      boostVisibility: false,
      polishWriting: false,
      fixGrammar: false,
      shortenIt: false
    });

    // Watch for when AI is enabled to track if it has been toggled on before
    watch(
      () => props.isAiEnabled,
      (newValue, oldValue) => {
        if (newValue) {
          hasBeenToggledOn.value = true;
        } else if (oldValue && hasBeenToggledOn.value) {
          // Only show toast if AI was previously enabled
          Toaster.notifySuccess(t('pages.aiTextEditor.toaster_ai_enabled_once'));
        }
      }
    );

    // Watch for replace actions from parent component
    watch(
      () => props.lastReplacedAction,
      actionType => {
        if (actionType && actionSuccessStates.value.hasOwnProperty(actionType)) {
          // Show success state for the replaced action
          actionSuccessStates.value[actionType] = true;

          // Hide success state after 2 seconds
          setTimeout(() => {
            actionSuccessStates.value[actionType] = false;
          }, AI_ACTION_MICRO_FEEDBACK_TIME);
        }
      }
    );

    const aIToggle = () => {
      emit('toggle-ai', !props.isAiEnabled);
    };

    // Computed property for dynamic tooltip content
    const aiTooltipContent = computed(() => {
      if (props.isAiEnabled) {
        // AI is currently enabled
        return '';
      } else if (hasBeenToggledOn.value) {
        // AI has been toggled on before but is now off
        return t('pages.aiTextEditor.ai_editor_tooltip_enabled_once');
      } else {
        // AI has never been toggled on
        return t('pages.aiTextEditor.ai_editor_tooltip_disabled');
      }
    });

    const isButtonsDisabled = computed(() => {
      return !props.isDiffView || props.isAiResponseLoading;
    });

    const isAiActionDisabled = computed(() => {
      return (
        props.characterCount.editor < MIN_LENGTH_AI_ENABLED ||
        props.isAiResponseLoading ||
        props.errorStatus ||
        props.isDiffView ||
        props.aiResponseError.disableActions
      );
    });

    const isShortenTextDisabled = computed(() => {
      return props.characterCount.editor < MIN_LENGTH_FOR_SHORTEN_TEXT;
    });

    const shouldShowAiActions = computed(() => {
      return props.config?.aiEnabled && props.isAiEnabled && props.characterCount.editor > 0;
    });

    const shouldShowAiResponseError = computed(() => {
      return (
        props.config?.aiEnabled &&
        props.isAiEnabled &&
        props.characterCount.editor > 0 &&
        props.aiResponseError.errorMessage
      );
    });

    return {
      hasBeenToggledOn,
      aiTooltipContent,
      shouldShowAiActions,
      actionSuccessStates,
      isButtonsDisabled,
      isAiActionDisabled,
      isShortenTextDisabled,
      shouldShowAiResponseError,
      aIToggle,
      showInfoModal,
      aiToggleOnColor: AI_TOGGLE_COLORS.ON,
      aiToggleOffColor: AI_TOGGLE_COLORS.OFF
    };
  }
};
